import { useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { WorkspaceBadge } from "@/components/ui/WorkspaceBadge";
import { SubmitTicketDialog } from "@/components/submit/SubmitTicketDialog";
import { useSubmitTargets } from "@/hooks/useSubmitTargets";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { resolveWorkspaceColor } from "@/lib/colors";
import { t } from "@/lib/strings";
import type { RequestForm } from "@/lib/types";

/**
 * Dashboard section that surfaces ticket-boards the user can submit to.
 * Hidden when there are no eligible boards — avoids a dead empty state on
 * project-only accounts.
 */
export function SubmitPickerSection() {
  const isDesktop = useBreakpoint("md");
  const { data: targets = [], isLoading } = useSubmitTargets();
  // The board the user is filing to — opens the submit form as a modal on top of
  // Home (blurred backdrop) rather than navigating away.
  const [active, setActive] = useState<RequestForm | null>(null);

  if (isLoading || targets.length === 0) return null;

  return (
    <div style={{ marginBottom: 36 }}>
      <div style={headerWrap}>
        <div>
          <div style={titleStyle}>{t.homeRequest}</div>
          <div style={subStyle}>{t.submitPickerSubtitle}</div>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isDesktop ? "repeat(3, 1fr)" : "1fr",
          gap: 12,
        }}
      >
        {targets.map((b) => (
          <SubmitCard key={b.id} board={b} onClick={() => setActive(b)} />
        ))}
      </div>

      <SubmitTicketDialog
        open={active !== null}
        onOpenChange={(o) => !o && setActive(null)}
        boardId={active?.id ?? null}
        boardName={active?.name}
        workspaceName={active?.workspace_name}
      />
    </div>
  );
}

function SubmitCard({ board, onClick }: { board: RequestForm; onClick: () => void }) {
  const color = resolveWorkspaceColor({ id: board.workspace_id, color: board.workspace_color });
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 16px",
        borderRadius: 10,
        border: "1px solid var(--hairline)",
        background: "var(--surface)",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        color: "var(--ink-1)",
        transition: "transform .12s ease, border-color .12s, background .12s",
        position: "relative",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--hairline-strong)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--hairline)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: color,
        }}
      />
      <WorkspaceBadge
        name={board.workspace_name}
        icon={board.workspace_icon}
        color={color}
        size={36}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ink-1)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {board.name}
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: "var(--ink-3)",
            marginTop: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {board.workspace_name}
        </div>
      </div>
      <Icon name="chevRight" size={14} color="var(--accent)" />
    </button>
  );
}

const headerWrap = {
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  margin: "0 0 12px",
} as const;

const titleStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: "var(--ink-1)",
  letterSpacing: "-0.01em",
} as const;

const subStyle = {
  fontSize: 12,
  color: "var(--ink-3)",
  marginTop: 2,
} as const;
